import { inject, Injectable } from '@angular/core';
import { DynamicDashboardWrapperHelperService } from '@dashboard/components/dynamic-dashboard-wrapper/dynamic-dashboard-wrapper-helper.service';
import { ToolControlKey } from '@dashboard/components/dynamic-dashboard-wrapper/tool-controls';
import { ExtendButton } from '@dashboard/components/extend-more-buttons/extend-more-butons.types';
import { SelectedVesselImo, SetRightBar } from '@dashboard/dashboard-store';
import { Store } from '@ngrx/store';
import { EnvVarsService } from '@root/app/env-vars.service';
import { DashboardConfigService } from '@root/app/services/dashboard-config/dashboard-config.service';
import { combineLatest, distinctUntilChanged, map, Observable } from 'rxjs';

@Injectable()
export class DashboardControlButtonsService {
  private _store = inject(Store);
  public helper = inject(DynamicDashboardWrapperHelperService);
  private appConfig = inject(DashboardConfigService);

  public $toolsControls = this._store.select(SelectedVesselImo).pipe(
    map((imo) => {
      const configControls = this.appConfig.getConfig().controls;

      const _defaultControls = this.DefaultControls(Number(imo))[0];

      // A key with no button here is skipped rather than rendered as an empty slot.
      const controls = configControls
        .filter((c) => c.show)
        .map((c) => _defaultControls.find((dc) => dc.id === c.key))
        .filter((control): control is ExtendButton<ToolControlKey> => !!control);

      return [controls];
    }),
    distinctUntilChanged()
  );

  private DefaultControls: (imo: number) => ExtendButton<ToolControlKey>[][] = (
    imo
  ) => [
    [
      {
        id: 'Voyage_Consumption_Estimator',
        tooltip: 'Voyage Consumption Estimator',
        icon: 'assets/icons/dashboard/tools/route-optimization.svg',
        action: () =>
          this._store.dispatch(SetRightBar({ selected: 'routeOptimazation' })),
        hide: false,
      },
      {
        id: 'Data_Export',
        tooltip: 'Data Export & Visualization',
        icon: 'assets/icons/dashboard/tools/export.svg',
        url: '/tools/export-tool',
        hide: false,
      },
      {
        id: 'Fuel_Curves',
        tooltip: 'Fuel Curves & References',
        icon: 'assets/icons/dashboard/tools/performance.svg',
        url: '/tools/performance-tool',
        hide: false,
      },
      {
        id: 'Wind_Assist_Experiments',
        tooltip: 'Wind Assist Experiments',
        icon: 'assets/icons/dashboard/tools/wind.svg',
        url: '/tools/wind-assistance',
        hide: false,
      },
      {
        id: 'Voyage_Experiments',
        tooltip: 'Voyage Experiments',
        icon: 'assets/icons/dashboard/tools/route-experiments.svg',
        url: '/tools/route-experiments',
        hide: false,
      },
      {
        id: 'Engine_Cooling_System_Optimization',
        tooltip: 'Engine Cooling System Optimization',
        icon: 'assets/icons/dashboard/tools/cooling.svg',
        url: '/tools/pump-vfd',
        hide: false,
      },
      {
        id: 'Charter_Party_Agreement',
        tooltip: 'Charter Party Agreement',
        icon: 'assets/icons/dashboard/tools/cpa.svg',
        url: '/navi-tools/cpa',
        action: () => {
          const url = '/navi-tools/cpa/cpa-terms-editor';
          const urlWithImo = imo ? `${url}?imo=${imo}` : url;
          window.open(urlWithImo, '_blank');
        },
        hide: false,
      },
      {
        id: 'Alarms_Monitoring_System',
        tooltip: 'Alarm & Monitoring System',
        icon: 'assets/icons/dashboard/tools/ams.svg',
        url: '/navi-tools/ams',
        action: () => {
          const url = '/navi-tools/ams';
          const urlWithImo = imo ? `${url}?imo=${imo}` : url;
          window.open(urlWithImo, '_blank');
        },
        hide: false,
      },
      {
        id: 'Fuel_Levels',
        tooltip: 'Fuel Levels',
        icon: 'assets/icons/dashboard/tools/fuel-levels.svg',
        url: '/navi-tools/fuel-levels',
        action: () => {
          const url = '/navi-tools/fuels/explorer';
          const urlWithImo = imo ? `${url}?imo=${imo}` : url;
          window.open(urlWithImo, '_blank');
        },
        hide: false,
      },

      {
        id: 'Noon_Reports',
        tooltip: 'Noon Reports',
        icon: 'assets/icons/dashboard/tools/cpa.svg',
        url: '/navi-tools/noon-reports',
        action: () => {
          const url = '/navi-tools/noon-reports';
          const urlWithImo = imo ? `${url}?imo=${imo}` : url;
          window.open(urlWithImo, '_blank');
        },
        hide: false,
      },
      {
        id: 'Vessel_Reports',
        tooltip: 'Vessel Reports',
        icon: 'assets/icons/dashboard/tools/performance.svg',
        url: '/navi-tools/vessel-reports',
        action: () => {
          // Vessel Reports keys vessels by IMO, so a selected vessel opens its own workspace;
          // otherwise the fleet overview.
          const url = imo
            ? `/navi-tools/vessel-reports/vessels/${imo}`
            : '/navi-tools/vessel-reports/overview';
          window.open(url, '_blank');
        },
        hide: false,
      },
    ],
  ];

  public $mapLayerControls: Observable<ExtendButton[][]> = combineLatest([
    this.helper.baseLayers$,
    this.helper.overlayLayers$,
    this.helper.VesselInfoLayers$,
    this.helper.WindLayers$,
  ]).pipe(
    map(([base, overlay, vesselInfo, wind]) => [
      [
        {
          id: 'base-maps',
          tooltip: 'Base Map',
          icon: 'assets/icons/dashboard/tools/map.svg',
          sideButtons: base.layers,
        },
      ],
      [
        {
          id: 'informative-layers',
          tooltip: 'Informative Layers',
          icon: 'assets/icons/dashboard/tools/info.svg',
          sideButtons: overlay.layers,
        },
      ],
      [
        {
          id: 'map-tools',
          tooltip: 'Map Tools',
          icon: 'assets/icons/dashboard/tools/toolbox.svg',
          sideButtons: vesselInfo,
        },
      ],
      [
        {
          id: 'weather-layers',
          tooltip: 'Weather Layers',
          icon: 'assets/icons/dashboard/tools/weather.svg',
          sideButtons: wind,
        },
      ],
    ])
  );
}
